<template>
  <content-dialog
    :visible.sync="modalVisible"
    max-width="650px"
    small-title
    persistent
    force-close-icon
  >
    <template v-slot:dialogTitle>
      <span>
        Send orders
      </span>
    </template>

    <v-card-text>
      <validation-observer ref="validationObserver">
        <validation-provider
          v-slot="{ errors }"
          rules="required|email"
        >
          <v-combobox
            v-model="to"
            multiple
            label="Recipient Emails"
            chips
            small-chips
            deletable-chips
            outlined
            :error-messages="errors"
            hint="Use Tab or Enter to add email"
          />
        </validation-provider>
        <validation-provider
          v-slot="{ errors }"
          rules="required"
        >
          <v-text-field
            v-model="subject"
            outlined
            dense
            label="Subject"
            :error-messages="errors"
          />
        </validation-provider>

        <validation-provider
          v-slot="{ errors }"
          rules="required"
        >
          <div class="editor">
            <input
              v-model="text"
              type="hidden"
            >
            <editor-menu-bar
              v-slot="{ commands, isActive }"
              :editor="editor"
            >
              <div class="grey lighten-2">
                <v-btn-toggle
                  dense
                  group
                  borderless
                  background-color="transparent"
                >
                  <v-btn
                    icon
                    :input-value="isActive.bold()"
                    @click="commands.bold()"
                  >
                    <v-icon>
                      format_bold
                    </v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    :input-value="isActive.italic()"
                    @click="commands.italic()"
                  >
                    <v-icon>
                      format_italic
                    </v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    :input-value="isActive.underline()"
                    @click="commands.underline()"
                  >
                    <v-icon>
                      format_underline
                    </v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    :input-value="isActive.strike()"
                    @click="commands.strike()"
                  >
                    <v-icon>
                      format_strikethrough
                    </v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    :input-value="isActive.blockquote()"
                    @click="commands.blockquote()"
                  >
                    <v-icon>
                      format_quote
                    </v-icon>
                  </v-btn>
                  <v-divider vertical />
                  <v-btn
                    icon
                    :input-value="isActive.bullet_list()"
                    @click="commands.bullet_list()"
                  >
                    <v-icon>
                      format_list_bulleted
                    </v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    :input-value="isActive.ordered_list()"
                    @click="commands.ordered_list()"
                  >
                    <v-icon>
                      format_list_numbered
                    </v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    :input-value="isActive.code_block()"
                    @click="commands.code_block()"
                  >
                    <v-icon>
                      code
                    </v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    :input-value="isActive.horizontal_rule()"
                    @click="commands.horizontal_rule()"
                  >
                    <v-icon>
                      horizontal_rule
                    </v-icon>
                  </v-btn>
                </v-btn-toggle>
              </div>
            </editor-menu-bar>
            <editor-content
              class="editor__content"
              :editor="editor"
            />
          </div>
          <span
            v-if="errors.length > 0"
            class="error--text caption"
          >
            {{ errors[0] }}
          </span>
        </validation-provider>
      </validation-observer>
    </v-card-text>

    <template
      v-slot:dialogActions
      class="align-start"
    >
      <v-col
        class="pr-6"
        cols="10"
      >
        <div
          v-for="attachment in attachments"
          :key="attachment.id"
          class="clickable px-3 py-1 grey lighten-3 blue--text caption elevation-2 rounded-l mb-1"
          @click="openDocument(attachment.file.id)"
        >
          <v-icon
            left
            small
          >
            download
          </v-icon>
          {{ attachment.file.original_file }}
        </div>
      </v-col>
      <v-spacer />
      <v-btn
        color="primary"
        :loading="sending"
        @click="send()"
      >
        <v-icon
          small
          left
        >
          send
        </v-icon>
        Send email
      </v-btn>
    </template>
  </content-dialog>
</template>

<script>
import ContentDialog from '@/components/helpers/Dialog'
import { Editor, EditorContent, EditorMenuBar } from 'tiptap'
import {
  Blockquote,
  Bold,
  BulletList,
  Code,
  CodeBlock,
  HardBreak,
  History,
  HorizontalRule,
  Italic,
  Link,
  ListItem,
  OrderedList,
  Placeholder,
  Strike,
  Underline
} from 'tiptap-extensions'
import YachtEquipmentPack from '@/models/YachtEquipmentPack'
import File from '@/models/File'

export default {
    name: 'SendDocumentsDialog',
    components: {
      ContentDialog,
      EditorContent,
      EditorMenuBar
    },
    props: {
      visible: Boolean,
      attachments: {
        type: Array,
        default: null
      },
      supplier: {
        type: Object,
        default: undefined
      }
    },
    data() {
      return {
        sending: false,
        subject: '',
        text: '',
        to: [],
        editor: new Editor({
          onUpdate: ({ getHTML }) => {
            this.text = getHTML()
          },
          extensions: [
            new HorizontalRule(),
            new Underline(),
            new Strike(),
            new BulletList(),
            new OrderedList(),
            new ListItem(),
            new HardBreak(),
            new Blockquote(),
            new History(),
            new Link(),
            new Placeholder({
              emptyNodeText: 'Enter your text here...'
            }),
            new Code(),
            new CodeBlock(),
            new Bold(),
            new Italic()
          ]
        })
      }
    },
    computed: {
      modalVisible: {
        get: function () {
          return this.visible
        },
        set: function (value) {
          this.$emit('update:visible', value)
        }
      },
      yachtModel() {
        return this.attachments[0] ? this.attachments[0].yacht.model.full_name : 'n/a'
      },
      hullNumber() {
        return this.attachments[0] ? (this.attachments[0].yacht.hull_number || 'n/a') : 'n/a'
      },
      yachtName() {
        return this.attachments[0] ? (this.attachments[0].yacht.name || 'n/a') : 'n/a'
      },
      shipBy() {
        return this.attachments[0] && this.attachments[0].yacht.ex_factory_date ? this.$moment(this.attachments[0].yacht.ex_factory_date).subtract(1, 'months').format('DD.MM.YYYY.') : 'n/a'
      },
      yacht() {
        return this.attachments[0] && this.attachments[0].equipment_pack ? this.attachments[0].equipment_pack.yacht : undefined
      },
      equipmentPack() {
        return this.attachments[0] ? this.attachments[0].equipment_pack : undefined
      }
    },
    watch: {
      modalVisible(value) {
        if (!value) return

        let email = this.supplier && this.supplier.contact_details ? this.supplier.contact_details.email : undefined
        // eslint-disable-next-line no-unused-vars
        let firstName = this.supplier && this.supplier.contact_details.first_name ? ' ' + this.supplier.contact_details.first_name : ''

        this.subject = 'Equipment order'
        if (email) this.to.push(email)

        // eslint-disable-next-line no-unused-vars
        let deliveryDetailsPart = 'n/a'
        if (this.equipmentPack && this.equipmentPack.installed_by === YachtEquipmentPack.INSTALLED_BY_CONTRACTOR) {
          deliveryDetailsPart = `<p>
<strong>${this.equipmentPack.contractor.name}</strong><br>
${this.equipmentPack.contractor.contact_details['address'] || ''}<br>
${this.equipmentPack.contractor.contact_details['city'] || ''}<br>
${this.equipmentPack.contractor.country.name}
</p>`
        } else if (this.equipmentPack && this.equipmentPack.installed_by === YachtEquipmentPack.INSTALLED_BY_NAVIGARE && this.yacht && (this.yacht.destination_base || this.yacht.current_base)) {
          deliveryDetailsPart = `<p>
<strong>${this.yacht.destination_base ? this.yacht.destination_base.name : this.yacht.current_base.name}</strong><br>
${this.yacht.destination_base ? this.yacht.destination_base.address : this.yacht.current_base.address}<br>
${this.yacht.destination_base ? this.yacht.destination_base.country.name : this.yacht.current_base.country.name}
</p>`
        } else if (this.equipmentPack && this.equipmentPack.installed_by === YachtEquipmentPack.INSTALLED_BY_SHIPYARD && this.yacht) {
          deliveryDetailsPart = `<p>
<strong>${this.yacht.model.manufacturer.name}</strong><br>
${this.yacht.model.manufacturer.country.name}
</p>`
        }

        this.editor.setContent(`<p>Hello${firstName},</p>
<p>Here attached is the equipment order for <strong>${this.yachtModel}</strong>, hull number <strong>${this.hullNumber}</strong>, name <strong>${this.yachtName}</strong>.</p>
<p>I would kindly ask you to ship this order by <strong>${this.shipBy}</strong> to:</p>${deliveryDetailsPart}
<p>Please make sure each package is marked with reference of the boat: <strong>ID #${this.yacht.id}</strong>, <strong>${this.yachtModel}</strong>, hull number <strong>${this.hullNumber}</strong>, name <strong>${this.yachtName}</strong></p>
<p>Kind regards,</p>`, true)
      }
    },
    created() {
    },
    methods: {
      openDocument(id) {
        File.api.openDocument(id)
      },
      async send() {
        let validation = await this.$refs.validationObserver.validate()

        if (!validation) return

        try {
          this.sending = true

          await YachtEquipmentPack.api.sendOrders({
            to: this.to,
            subject: this.subject,
            text: this.text,
            attachments: this.attachments
          })

          this.$store.dispatch('alert', {
            text: 'Order successfully sent!',
            color: 'success'
          })

          this.$emit('order:sent')

          this.modalVisible = false
        } catch (e) {
          if (e.response && e.response.data && e.response.status === 422) {
            this.$store.dispatch('alert', {
              text: Object.values(e.response.data.errors).join(' '),
              color: 'error'
            })
            return
          }

          this.$store.dispatch('alert', {
            text: 'Something went wrong',
            color: 'error'
          })

          throw e
        } finally {
        this.sending = false
        }
      }
    }
  }
</script>

<style lang="scss">
  @import "~vuetify/src/components/VStepper/VStepper";

  .v-stepper__step--vertical .v-stepper__label {
    width: 100%;
    flex-direction: row;
  }
</style>
